import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import platformApiService, { Invoice } from '../../services/platformApi';
import { CurrencyDollarIcon, DocumentPlusIcon, XMarkIcon } from '@heroicons/react/24/outline';

const centsToRands = (cents: number) => `R${(cents / 100).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : '—');

const INVOICE_STATUS_STYLES: Record<string, string> = {
  draft: 'bg-slate-500/10 text-slate-400 ring-1 ring-slate-500/30',
  issued: 'bg-sky-500/10 text-sky-300 ring-1 ring-sky-500/30',
  paid: 'bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30',
  void: 'bg-red-500/10 text-red-300 ring-1 ring-red-500/30',
};

const PlatformBilling: React.FC = () => {
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null);

  const overviewQuery = useQuery({
    queryKey: ['platform-billing-overview'],
    queryFn: async () => {
      const response = await platformApiService.billingOverview();
      if (!response.success) throw new Error(response.error?.message || 'Failed to load usage overview');
      return response.data!;
    },
  });

  const invoicesQuery = useQuery({
    queryKey: ['platform-invoices'],
    queryFn: async () => {
      const response = await platformApiService.listInvoices();
      if (!response.success) throw new Error(response.error?.message || 'Failed to load invoices');
      return response.data || [];
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Invoice['status'] }) => {
      const response = await platformApiService.setInvoiceStatus(id, status);
      if (!response.success) throw new Error(response.error?.message || 'Failed to update invoice');
      return response.data!;
    },
    onSuccess: (invoice) => {
      queryClient.invalidateQueries({ queryKey: ['platform-invoices'] });
      setActiveInvoice(invoice);
    },
  });

  const totals = useMemo(() => {
    const rows = overviewQuery.data?.municipalities || [];
    return {
      estimatedCents: rows.reduce((sum, r) => sum + r.estimatedCents, 0),
      sms: rows.reduce((sum, r) => sum + r.smsUsed, 0),
      push: rows.reduce((sum, r) => sum + r.pushUsed, 0),
    };
  }, [overviewQuery.data]);

  const period = overviewQuery.data?.period;

  const handleGenerate = async (municipalityId: string) => {
    setGenerating(municipalityId);
    try {
      const response = await platformApiService.generateInvoice(municipalityId, period?.from, period?.to);
      if (!response.success || !response.data) throw new Error(response.error?.message || 'Failed to generate invoice');
      queryClient.invalidateQueries({ queryKey: ['platform-invoices'] });
      setActiveInvoice(response.data);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Failed to generate invoice');
    } finally {
      setGenerating(null);
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-white">Billing &amp; Usage</h1>
        <p className="text-sm text-slate-500">
          {period ? `Current period: ${fmtDate(period.from)} – ${fmtDate(period.to)}` : 'Metered SMS and push usage across every tenant.'}
        </p>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 px-4 py-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-800">
            <CurrencyDollarIcon className="h-4.5 w-4.5 text-slate-400" />
          </div>
          <div>
            <div className="text-lg font-semibold text-white">{centsToRands(totals.estimatedCents)}</div>
            <div className="text-xs text-slate-500">Estimated MRR this period</div>
          </div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-3">
          <div className="text-lg font-semibold text-white">{totals.sms.toLocaleString()}</div>
          <div className="text-xs text-slate-500">SMS sent (registration OTP)</div>
        </div>
        <div className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-3">
          <div className="text-lg font-semibold text-white">{totals.push.toLocaleString()}</div>
          <div className="text-xs text-slate-500">Push notifications sent</div>
        </div>
      </div>

      {toast && <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{toast}</div>}

      <div className="mb-8 overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
        <div className="border-b border-slate-800 px-4 py-3 text-sm font-semibold text-white">Usage by municipality</div>
        {overviewQuery.isLoading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading usage…</div>
        ) : overviewQuery.error ? (
          <div className="p-8 text-center text-sm text-red-300">{(overviewQuery.error as Error).message}</div>
        ) : (overviewQuery.data?.municipalities.length || 0) === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No municipalities yet.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-medium">Municipality</th>
                <th className="px-4 py-3 font-medium">Plan</th>
                <th className="px-4 py-3 font-medium">SMS used</th>
                <th className="px-4 py-3 font-medium">Push used</th>
                <th className="px-4 py-3 font-medium">Estimated this period</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {overviewQuery.data!.municipalities.map((row) => (
                <tr key={row.municipalityId} className="border-b border-slate-800/60 last:border-0 hover:bg-slate-800/40">
                  <td className="px-4 py-3">
                    <div className="font-medium text-white">{row.municipalityName}</div>
                    <div className="text-xs text-slate-500">{row.municipalityCode}</div>
                  </td>
                  <td className="px-4 py-3 capitalize text-slate-300">{row.planLabel}</td>
                  <td className="px-4 py-3 text-slate-300">
                    {row.smsUsed}
                    {row.smsOverage > 0 && <span className="ml-1 text-xs text-amber-300">(+{row.smsOverage} over)</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-300">
                    {row.pushUsed}
                    {row.pushOverage > 0 && <span className="ml-1 text-xs text-amber-300">(+{row.pushOverage} over)</span>}
                  </td>
                  <td className="px-4 py-3 font-medium text-white">{centsToRands(row.estimatedCents)}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleGenerate(row.municipalityId)}
                      disabled={generating === row.municipalityId}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-emerald-500 hover:text-emerald-300 disabled:opacity-50"
                    >
                      <DocumentPlusIcon className="h-3.5 w-3.5" />
                      {generating === row.municipalityId ? 'Generating…' : 'Generate invoice'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
        <div className="border-b border-slate-800 px-4 py-3 text-sm font-semibold text-white">Invoices</div>
        {invoicesQuery.isLoading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading invoices…</div>
        ) : invoicesQuery.error ? (
          <div className="p-8 text-center text-sm text-red-300">{(invoicesQuery.error as Error).message}</div>
        ) : (invoicesQuery.data?.length || 0) === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No invoices generated yet.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-medium">Municipality</th>
                <th className="px-4 py-3 font-medium">Period</th>
                <th className="px-4 py-3 font-medium">Subtotal</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Generated</th>
                <th className="px-4 py-3 font-medium text-right">View</th>
              </tr>
            </thead>
            <tbody>
              {invoicesQuery.data!.map((inv) => (
                <tr key={inv.id} className="border-b border-slate-800/60 last:border-0 hover:bg-slate-800/40">
                  <td className="px-4 py-3 text-white">{inv.municipality_name || '—'}</td>
                  <td className="px-4 py-3 text-slate-300">{fmtDate(inv.period_start)} – {fmtDate(inv.period_end)}</td>
                  <td className="px-4 py-3 font-medium text-white">{centsToRands(inv.subtotal_cents)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${INVOICE_STATUS_STYLES[inv.status]}`}>{inv.status}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{fmtDate(inv.generated_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setActiveInvoice(inv)} className="text-xs text-slate-300 underline hover:text-emerald-300">
                      Details
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {activeInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-lg rounded-xl border border-slate-800 bg-slate-900 p-5 shadow-xl">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <h3 className="text-sm font-semibold text-white">{activeInvoice.municipality_name}</h3>
                <p className="text-xs text-slate-500">
                  {fmtDate(activeInvoice.period_start)} – {fmtDate(activeInvoice.period_end)} · {activeInvoice.currency}
                </p>
              </div>
              <button onClick={() => setActiveInvoice(null)} className="text-slate-500 hover:text-slate-300">
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-3 space-y-2 rounded-lg border border-slate-800 bg-slate-950 p-3">
              {activeInvoice.line_items.length === 0 ? (
                <p className="text-sm text-slate-500">No billable line items for this period.</p>
              ) : (
                activeInvoice.line_items.map((li, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-slate-300">{li.description}</span>
                    <span className="font-medium text-white">{centsToRands(li.totalCents)}</span>
                  </div>
                ))
              )}
              <div className="flex items-center justify-between border-t border-slate-800 pt-2 text-sm font-semibold">
                <span className="text-slate-200">Subtotal</span>
                <span className="text-white">{centsToRands(activeInvoice.subtotal_cents)}</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${INVOICE_STATUS_STYLES[activeInvoice.status]}`}>
                {activeInvoice.status}
              </span>
              <div className="flex gap-2">
                {activeInvoice.status === 'draft' && (
                  <button
                    onClick={() => statusMutation.mutate({ id: activeInvoice.id, status: 'issued' })}
                    disabled={statusMutation.isPending}
                    className="rounded-md bg-sky-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-sky-400 disabled:opacity-60"
                  >
                    Mark issued
                  </button>
                )}
                {activeInvoice.status === 'issued' && (
                  <button
                    onClick={() => statusMutation.mutate({ id: activeInvoice.id, status: 'paid' })}
                    disabled={statusMutation.isPending}
                    className="rounded-md bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60"
                  >
                    Mark paid
                  </button>
                )}
                {activeInvoice.status !== 'void' && activeInvoice.status !== 'paid' && (
                  <button
                    onClick={() => statusMutation.mutate({ id: activeInvoice.id, status: 'void' })}
                    disabled={statusMutation.isPending}
                    className="rounded-md border border-red-500/40 px-3 py-1.5 text-xs font-semibold text-red-300 hover:bg-red-500/10 disabled:opacity-60"
                  >
                    Void
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformBilling;
